// MP424 — one error path for every web client wrapper.
//
// The api answers errors in three shapes:
//
//   { message }                 fastify-sensible / most routes
//   { ok: false, error }        vendor install + a few posture routes
//   { message, issues[] }       the global Zod handler ("Validation failed")
//
// A wrapper that reads only `message` shows the operator "HTTP 400"
// while the api's body holds Azure AD's exact reason (2026-09-22,
// Intune wizard) — or a bare "Validation failed" with the per-field
// detail dropped (memory: feedback_surface_zod_issues_in_client).
//
// `jsonOrThrow` reads message → error → `HTTP <status>`, appends any
// walked issues as `path: message; …`, and returns the parsed body on
// 2xx (204 → undefined). Modelled on `request()` in surfaceUam.ts.
//
// Every wrapper under apps/web/src/lib throws through this module —
// either `jsonOrThrow(res)` directly, or `readApiError(res)` /
// `apiErrorFromBody(status, body)` when the wrapper has already
// consumed (or must not consume) the response body.

export interface ApiIssue {
  path: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly issues: ApiIssue[];

  constructor(message: string, status: number, code: string | null = null, issues: ApiIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

const nonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

const issuePath = (p: unknown): string => {
  if (Array.isArray(p)) return p.length ? p.map(String).join('.') : '(root)';
  return nonEmptyString(p) ? p : '(root)';
};

/** Build an ApiError from an already-parsed (or unparseable → `{}` / null) error body. */
export function apiErrorFromBody(status: number, body: unknown): ApiError {
  const b = (body && typeof body === 'object' ? body : {}) as {
    message?: unknown;
    error?: unknown;
    code?: unknown;
    issues?: unknown;
  };
  const issues: ApiIssue[] = (Array.isArray(b.issues) ? b.issues : [])
    .filter(
      (i): i is { path?: unknown; code?: unknown; message: string } =>
        !!i && typeof i === 'object' && nonEmptyString((i as { message?: unknown }).message),
    )
    .map((i) => ({
      path: issuePath(i.path),
      code: nonEmptyString(i.code) ? i.code : 'invalid',
      message: i.message,
    }));
  const head = nonEmptyString(b.message)
    ? b.message
    : nonEmptyString(b.error)
      ? b.error
      : `HTTP ${status}`;
  const detail = issues.length ? ` — ${issues.map((i) => `${i.path}: ${i.message}`).join('; ')}` : '';
  return new ApiError(`${head}${detail}`, status, nonEmptyString(b.code) ? b.code : null, issues);
}

/** Read a non-2xx response's body and build the ApiError (never throws itself). */
export async function readApiError(res: Response): Promise<ApiError> {
  const body: unknown = await res.json().catch(() => ({}));
  return apiErrorFromBody(res.status, body);
}

/**
 * Parse a JSON response, throwing an ApiError on non-2xx. 204 → undefined.
 * `T` defaults to `any` so the ~30 wrappers that previously used an
 * untyped local `jsonOrThrow` keep their exact call-site typing.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function jsonOrThrow<T = any>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => ({}));
  if (!res.ok) throw apiErrorFromBody(res.status, body);
  return body as T;
}

/** Plain words for a failed load or action, never the raw error text
 *  (raw messages can carry internals such as database or console
 *  details). Shown as-is: a 400's field issues (named in plain words),
 *  or the message of a deliberate 400 / 403 / 404 / 409 refusal, which
 *  the api writes to be read. Anything else (5xx, network, a bare
 *  `HTTP <status>`, a non-api error) becomes `fallback`. */
export function friendlyError(err: unknown, fallback: string): string {
  const e = err as { status?: unknown; issues?: unknown; message?: unknown } | null;
  // A 400 from a form names the field in plain words; show those.
  if (e && e.status === 400) {
    const issues = Array.isArray(e.issues) ? (e.issues as Array<{ message?: unknown }>).map((i) => i.message).filter((m): m is string => typeof m === 'string') : [];
    if (issues.length > 0) return issues.join(' ');
    if (typeof e.message === 'string' && e.message && !e.message.startsWith('HTTP ')) return e.message;
  }
  if (e && (e.status === 403 || e.status === 404 || e.status === 409) && typeof e.message === 'string' && e.message && !e.message.startsWith('HTTP ')) return e.message;
  return fallback;
}
